import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { Calendar } from './ui/calendar';
import { Wrench, Calendar as CalendarIcon, User, PlusCircle } from 'lucide-react';
import { maintenanceSchedule as initialMaintenanceSchedule, sites, districts } from '../mock';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Maintenance = () => {
  const navigate = useNavigate();
  const [maintenanceSchedule, setMaintenanceSchedule] = useState(initialMaintenanceSchedule);
  const [date, setDate] = useState(new Date());

  const [newActivity, setNewActivity] = useState({
    id: `PM${String(maintenanceSchedule.length + 1).padStart(3, '0')}`,
    site: '',
    district: '',
    type: '',
    scheduledDate: new Date(),
    technician: '',
    status: 'scheduled',
  });

  const handleAddActivity = () => {
    if (newActivity.site && newActivity.type && newActivity.technician) {
      const siteDetails = sites.find(s => s.id === newActivity.site);
      const activityToAdd = { ...newActivity, district: siteDetails.district };

      setMaintenanceSchedule([activityToAdd, ...maintenanceSchedule]);
      setNewActivity({
        id: `PM${String(maintenanceSchedule.length + 2).padStart(3, '0')}`,
        site: '',
        district: '',
        type: '',
        scheduledDate: new Date(),
        technician: '',
        status: 'scheduled',
      });
    }
  };


  const getStatusColor = (status) => {
    if (status === 'completed') return { bg: '#e8f5f1', color: '#10ac84', border: '#10ac84' };
    if (status === 'upcoming') return { bg: '#fff4e6', color: '#f39c12', border: '#f39c12' };
    return { bg: '#e3f2fd', color: '#3498db', border: '#3498db' };
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>Preventive Maintenance</h1>
        <p className="text-base mt-2" style={{ color: '#576574' }}>Schedule and track preventive maintenance activities</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{maintenanceSchedule.length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Total Activities</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#3498db' }}>{maintenanceSchedule.filter(m => m.status === 'scheduled').length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Scheduled</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#f39c12' }}>{maintenanceSchedule.filter(m => m.status === 'upcoming').length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Upcoming</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#10ac84' }}>{maintenanceSchedule.filter(m => m.status === 'completed').length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Completed</div>
          </CardContent>
        </Card>
      </div>

      {/* Maintenance Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Maintenance Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Pie
                data={{
                  labels: ['Scheduled', 'Upcoming', 'Completed'],
                  datasets: [{
                    data: [
                      maintenanceSchedule.filter(m => m.status === 'scheduled').length,
                      maintenanceSchedule.filter(m => m.status === 'upcoming').length,
                      maintenanceSchedule.filter(m => m.status === 'completed').length
                    ],
                    backgroundColor: [
                      'rgba(52, 152, 219, 0.8)',
                      'rgba(243, 156, 18, 0.8)',
                      'rgba(16, 172, 132, 0.8)'
                    ],
                    borderColor: [
                      '#3498db',
                      '#f39c12',
                      '#10ac84'
                    ],
                    borderWidth: 1,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Maintenance Types</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Bar
                data={{
                  labels: [...new Set(maintenanceSchedule.map(m => m.type))],
                  datasets: [{
                    label: 'Activities',
                    data: [...new Set(maintenanceSchedule.map(m => m.type))].map(type =>
                      maintenanceSchedule.filter(m => m.type === type).length
                    ),
                    backgroundColor: 'rgba(155, 89, 182, 0.8)',
                    borderColor: '#9b59b6',
                    borderWidth: 1,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        stepSize: 1,
                        color: '#576574',
                      },
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      }
                    },
                    x: {
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                      }
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar View */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" style={{ color: '#3498db' }} />
              Schedule Calendar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              className="rounded-md border"
            />
            <div className="mt-6 space-y-3">
              <div className="text-sm font-semibold" style={{ color: '#0a3d62' }}>Legend</div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#3498db' }} />
                <span className="text-sm" style={{ color: '#576574' }}>Scheduled</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#f39c12' }} />
                <span className="text-sm" style={{ color: '#576574' }}>Upcoming</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#10ac84' }} />
                <span className="text-sm" style={{ color: '#576574' }}>Completed</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Maintenance Schedule List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Maintenance Activities</CardTitle>
              <Dialog>
                <DialogTrigger asChild>
                  <Button>
                    <PlusCircle className="mr-2 h-4 w-4" /> Schedule New
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>Schedule New Maintenance</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="site" className="text-right">Site</Label>
                      <Select onValueChange={(value) => setNewActivity({ ...newActivity, site: value })}>
                        <SelectTrigger className="col-span-3"><SelectValue placeholder="Select Site" /></SelectTrigger>
                        <SelectContent>
                          {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="type" className="text-right">Activity Type</Label>
                      <Select onValueChange={(value) => setNewActivity({ ...newActivity, type: value })}>
                        <SelectTrigger className="col-span-3"><SelectValue placeholder="Select Type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Panel Cleaning">Panel Cleaning</SelectItem>
                          <SelectItem value="Inverter Inspection">Inverter Inspection</SelectItem>
                          <SelectItem value="Complete System Check">Complete System Check</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="date" className="text-right">Date</Label>
                      <Calendar
                        mode="single"
                        selected={newActivity.scheduledDate}
                        onSelect={(date) => setNewActivity({ ...newActivity, scheduledDate: date })}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="technician" className="text-right">Technician</Label>
                      <Input id="technician" value={newActivity.technician} onChange={(e) => setNewActivity({ ...newActivity, technician: e.target.value })} className="col-span-3" />
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button type="button" onClick={handleAddActivity}>Schedule</Button>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {maintenanceSchedule.map((activity) => {
                const site = sites.find(s => s.id === activity.site);
                const district = districts.find(d => d.id === activity.district);
                const statusStyle = getStatusColor(activity.status);
                
                return (
                  <div key={activity.id} onClick={() => navigate(`/site/${activity.site}`)} className="p-4 rounded-lg border hover:shadow-md transition-shadow cursor-pointer">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: statusStyle.bg }}>
                          <Wrench className="w-5 h-5" style={{ color: statusStyle.color }} />
                        </div>
                        <div>
                          <div className="font-mono text-sm font-semibold" style={{ color: '#0a3d62' }}>{activity.id}</div>
                          <div className="text-sm" style={{ color: '#576574' }}>{activity.type}</div>
                        </div>
                      </div>
                      <Badge variant="outline" style={statusStyle}>
                        {activity.status.toUpperCase()}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-xs mb-1" style={{ color: '#576574' }}>Site</div>
                        <div className="font-semibold text-sm font-mono">{activity.site}</div>
                        <div className="text-xs" style={{ color: '#576574' }}>{site?.name}</div>
                      </div>
                      <div>
                        <div className="text-xs mb-1" style={{ color: '#576574' }}>District</div>
                        <div className="font-semibold text-sm capitalize">{district?.name}</div>
                      </div>
                      <div>
                        <div className="text-xs mb-1" style={{ color: '#576574' }}>Scheduled Date</div>
                        <div className="font-semibold text-sm">{new Date(activity.scheduledDate).toLocaleDateString()}</div>
                      </div>
                      <div>
                        <div className="text-xs mb-1" style={{ color: '#576574' }}>Technician</div>
                        <div className="font-semibold text-sm flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {activity.technician}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Maintenance Types */}
      <Card>
        <CardHeader>
          <CardTitle>Maintenance Activity Types</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
              <div className="font-semibold mb-2" style={{ color: '#0a3d62' }}>Panel Cleaning</div>
              <div className="text-sm" style={{ color: '#576574' }}>Regular cleaning of solar panels to maintain efficiency</div>
              <div className="mt-3 text-sm">
                <span style={{ color: '#576574' }}>Frequency: </span>
                <span className="font-semibold">Monthly</span>
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
              <div className="font-semibold mb-2" style={{ color: '#0a3d62' }}>Inverter Inspection</div>
              <div className="text-sm" style={{ color: '#576574' }}>Check inverter health and performance parameters</div>
              <div className="mt-3 text-sm">
                <span style={{ color: '#576574' }}>Frequency: </span>
                <span className="font-semibold">Quarterly</span>
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
              <div className="font-semibold mb-2" style={{ color: '#0a3d62' }}>Complete System Check</div>
              <div className="text-sm" style={{ color: '#576574' }}>Comprehensive inspection of entire solar installation</div>
              <div className="mt-3 text-sm">
                <span style={{ color: '#576574' }}>Frequency: </span>
                <span className="font-semibold">Bi-Annual</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Maintenance;
